import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  AtSign, User, Loader2, MessageCircle, Clock, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MentionsPage() {
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadMentions();
  }, []);

  const loadMentions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/chat/mentions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMentions(await res.json());
      }
    } catch (error) {
      console.error('Failed to load mentions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <DashboardLayout>
      <div className="p-6 pt-8" data-testid="mentions-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <AtSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Mentions</h1>
              <p className="text-gray-400">
                {mentions.length} {mentions.length === 1 ? 'mention' : 'mentions'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : mentions.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <AtSign className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No mentions yet</h3>
            <p className="text-gray-400 mb-6 max-w-sm mx-auto">
              When someone mentions you in a message using @, it will appear here.
            </p>
            <Link to="/messages">
              <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
                Go to Messages
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {mentions.map((mention) => (
              <motion.div
                key={mention.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-xl p-5 hover:border-purple-500/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Sender Avatar */}
                  {mention.sender_avatar ? (
                    <img 
                      src={mention.sender_avatar} 
                      alt={mention.sender_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-white" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-white">{mention.sender_name}</p>
                      <span className="text-purple-400 text-sm">mentioned you</span>
                    </div>
                    <p className="text-gray-300 mb-3 line-clamp-2">{mention.content}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Clock className="w-4 h-4" />
                        {formatTime(mention.created_at)}
                      </div>
                      <Link to={`/messages`}>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          View Conversation
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
